// src/utils/thumbnailUrl.js
import { buildAuthUrl } from './buildAuthUrl';

/**
 * The thumbnail endpoint answers with `Cache-Control: public, max-age=31536000,
 * immutable`, which is the right call — thumbnails are derived bytes and
 * re-fetching one per tile on every grid render would be wasteful. But the URL
 * only ever carried the file id, so a REPLACED thumbnail (the 3D viewer's
 * capture button, or "Regenerate thumbnail") stayed invisible behind that cache
 * until a hard reload.
 *
 * Folding the current thumbnail's identity into the query gives every version
 * its own URL: unchanged tiles still hit the year-long cache, and a new one is a
 * cache miss by construction. The backend ignores the extra param.
 */

/** Identity of the thumbnail bytes a file currently points at, or null. */
export function thumbnailCacheKey(file) {
    const thumbnail = file?.thumbnail;
    if (!thumbnail) return null;

    // `timestamp` reaches the client as the ISO string JSON.stringify made of the
    // Date the service constructed. Records written straight through the Admin
    // SDK can arrive as a Firestore timestamp shape instead.
    const stamp = thumbnail.timestamp;
    if (typeof stamp === 'string' && stamp) return stamp;
    if (stamp && typeof stamp === 'object') {
        const seconds = stamp._seconds ?? stamp.seconds;
        if (seconds != null) return String(seconds);
    }
    // Custom uploads reuse one object name (`<id>_custom.png`) so this is a weak
    // key for them, but it still beats nothing for pre-timestamp records.
    return thumbnail.gcsId ? String(thumbnail.gcsId) : null;
}

/** Authenticated <img> src for a file's thumbnail, versioned by its contents. */
export function buildThumbnailUrl(file) {
    const version = thumbnailCacheKey(file);
    return buildAuthUrl('/api/files/thumbnail', {
        id: file.id,
        ...(version ? { v: version } : {}),
    });
}
